import { useEffect, useState, useCallback } from "react";
import { Volume2, VolumeX, Radio } from "lucide-react";
import { audioBus, hapticTap, type SoundMode } from "@/lib/audioBus";

interface Props {
  isTransparent?: boolean;
  borderColor?: string;
  textColor?: string;
  variant?: "capsule" | "menu";
}

// Cycle: Showroom → Tactile → Off → Showroom …
// Internal mode keys remain for backward storage compatibility:
//   immersive ↔ Showroom, subtle ↔ Tactile, off ↔ Off.
const NEXT: Record<SoundMode, SoundMode> = {
  immersive: "subtle",
  subtle: "off",
  off: "immersive",
};

const LABEL: Record<SoundMode, string> = {
  off: "Off",
  subtle: "Tactile",
  immersive: "Showroom",
};

const SoundToggle = ({ isTransparent, borderColor, textColor, variant = "capsule" }: Props) => {
  const [mode, setMode] = useState<SoundMode>(() => audioBus.getMode());

  useEffect(() => {
    return audioBus.subscribe(() => setMode(audioBus.getMode()));
  }, []);

  const cycle = useCallback(() => {
    const next = NEXT[audioBus.getMode()];
    audioBus.setMode(next);
    setMode(next);
    hapticTap("light");
    if (next !== "off") audioBus.play("click");
  }, []);

  const enabled = mode !== "off";
  const ariaLabel = `${LABEL[mode]} (click to switch)`;

  if (variant === "menu") {
    return (
      <button
        type="button"
        onClick={cycle}
        aria-pressed={enabled}
        aria-label={ariaLabel}
        className="flex items-center gap-3 text-sm tracking-wide text-foreground/85 hover:text-foreground transition-colors min-h-[44px]"
      >
        {mode === "off" ? (
          <VolumeX className="h-4 w-4" />
        ) : mode === "subtle" ? (
          <Volume2 className="h-4 w-4" />
        ) : (
          <Radio className="h-4 w-4" />
        )}
        <span>{LABEL[mode]}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={cycle}
      aria-pressed={enabled}
      aria-label={ariaLabel}
      title={ariaLabel}
      className="liquid-capsule flex h-8 w-8 items-center justify-center rounded-full border border-transparent transition-all duration-token-base"
      style={{
        color: textColor ?? (isTransparent ? "hsl(var(--foreground) / 0.94)" : "hsl(var(--foreground) / 0.90)"),
        borderColor: borderColor ?? "transparent",
      }}
    >
      {mode === "off" ? (
        <VolumeX className="h-3.5 w-3.5" />
      ) : mode === "subtle" ? (
        <Volume2 className="h-3.5 w-3.5" />
      ) : (
        <Radio className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

export default SoundToggle;
